(ns app.front.view.application.phase-subs
  (:require [app.front.entities :as entities]
            [app.front.view.navigation :as navigation]
            [app.front.view.workspace.subs :as ws-subs]
            [app.front.view.workspace.util :as ws-util]
            [core.front.icons :as i]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))

(defn phase->index [phases phase]
  (p/find-index phases #(= % phase)))

(def phase-routes
  "Routes, which lead to the main workspace-page with a specific phase
  selected."
  [:view/workspace-pre-consultation
   :view/workspace-application
   :view/workspace-execution
   :view/workspace-archiving])

(defn can-access-execution?
  "Is the workspace in a state where execution link is clickable?

  Execution phase may exists before we wish to make it visible."
  [workspace-state execution]
  (and (#{:workspace.state/execution :workspace.state/archived}
        workspace-state)
       (some? execution)))

(rf/reg-event-fx ::redirect-to-current-phase
  (fn [{:keys [db]} [_ workspace-id workspace-state]]
    (let [route-match      (get-in db [:route/match :data :name])
          ents             (-> db ::entities/entity-db vals)
          workspace-eid    (-> ents
                               (p/find-first {:workspace/id workspace-id})
                               :db/id)
          find-child       (fn [child-type]
                             (-> ents
                                 (p/find-first {:entity/parent workspace-eid
                                                :entity/type   child-type})))
          available-states (->> [(when (find-child :entity.type/pre-consultation)
                                   :view/workspace-pre-consultation)
                                 (when (find-child :entity.type/application)
                                   :view/workspace-application)
                                 (when (some->> (find-child :entity.type/execution)
                                                (can-access-execution? workspace-state))
                                   :view/workspace-execution)
                                 (when (find-child :entity.type/periodic-parent)
                                   :view/workspace-periodic-parent)
                                 (when (find-child :entity.type/archiving)
                                   :view/workspace-archiving)]
                                (filter some?)
                                set)
          ;; Route is either the correct one from available-states, or the
          ;; last of them. This happens when the user does not have access
          ;; to the current phase-entity (i.e. is no longer a party).
          route            (or (available-states
                                 (case workspace-state
                                   :workspace.state/pre-consultation :view/workspace-pre-consultation
                                   :workspace.state/application :view/workspace-application
                                   :workspace.state/execution :view/workspace-execution
                                   :workspace.state/periodic-parent :view/workspace-periodic-parent
                                   :workspace.state/archived :view/workspace-archiving
                                   nil))
                               (when workspace-state
                                 ;; do not short-circuit to default when requested state not
                                 ;; yet known (workspace fetch pending)
                                 (last available-states)))]
      (when (and route
                 (or
                   ;; from /workspace/ABC-123/ , redirect to /application, /execution etc as applicable
                   (= route-match :view/workspace)
                   ;; from /workspace/ABC-123/invalid-phase, redict to current applicable phase, e.g.
                   ;; when trying to use /execution in the URL when workspace is not yet in that phase
                   (and (not (contains? available-states route-match))
                        ;; ... but only when we are actually going to a phase route. A counter example
                        ;; would be the inspection visit page.
                        (contains? (set phase-routes) route-match))))
        {:route/replace (p/assoc-if {:route route :params {:workspace-id workspace-id}}
                                    :query (ws-util/search-origin-query-params (:route/match db)))}))))

(defn phase-as-map [tr include-phase? name icon enabled? view-name & [title-tr-key]]
  (when include-phase?
    (util/mmap {:title (tr [(or title-tr-key name)])}
               name icon enabled? view-name)))

(rf/reg-sub ::can-access-execution?
  :<- [::ws-subs/workspace-state]
  :<- [::ws-subs/execution]
  (fn [[state execution] _]
    (can-access-execution? state execution)))

(rf/reg-sub ::include-pre-consultation?
  :<- [::ws-subs/pre-consultation]
  :-> some?)

(rf/reg-sub ::include-application?
  :<- [::ws-subs/application]
  :-> some?)

(rf/reg-sub ::include-execution?
  :<- [::ws-subs/execution]
  :<- [::include-application?]
  :<- [::ws-subs/omit-execution?]
  (fn [[execution include-application? omit-execution?] _]
    (or (and (not omit-execution?)
             ;; in P workspaces, application phase may not happen, neither fullfillment
             include-application?)
        (some? execution))))

(rf/reg-sub ::include-periodic-parent?
  :<- [::ws-subs/periodic-parent]
  :-> some?)

(rf/reg-sub ::include-archiving?
  :<- [::ws-subs/archiving]
  :-> some?)

(rf/reg-sub ::phases
  :<- [::include-pre-consultation?]
  :<- [::ws-subs/prior-pre-consultation]
  :<- [::include-application?]
  :<- [::include-execution?]
  :<- [::include-periodic-parent?]
  :<- [::include-archiving?]
  :<- [::can-access-execution?]
  :<- [::ws-subs/tr]
  (fn [[include-pre-consultation?
        prior-pre-consultation
        include-application?
        include-execution?
        include-periodic-parent?
        include-archiving?
        can-access-execution?
        tr] _]
    (let [phase-as-map (partial phase-as-map tr)]
      (->> [(phase-as-map include-pre-consultation?
                          :workspace.state/pre-consultation
                          i/persons
                          true
                          :view/workspace-pre-consultation)
            (when (and (not include-pre-consultation?)
                       prior-pre-consultation)
              {:title    (tr [:workspace.state/pre-consultation])
               :name     :workspace.state/pre-consultation
               :href     (-> prior-pre-consultation :entity/parent ws-util/pre-consultation-href)
               :icon     i/persons
               :enabled? true})
            (phase-as-map include-application?
                          :workspace.state/application
                          i/document
                          true
                          :view/workspace-application)
            (phase-as-map include-execution?
                          :workspace.state/execution
                          i/building
                          can-access-execution?
                          :view/workspace-execution
                          :application/working)
            (phase-as-map include-periodic-parent?
                          :workspace.state/periodic-parent
                          i/building
                          true
                          :view/workspace-periodic-parent)
            (phase-as-map include-archiving?
                          :workspace.state/archiving
                          i/archives
                          true
                          :view/workspace-archiving)]
           (filter some?)
           (map-indexed #(assoc %2 :index %1))
           vec))))

(rf/reg-event-fx ::set-phase-in-view
  (fn [{{:keys [:route/match] :as db} :db}
       [_ next-route]]
    (let [current-route (-> match :data :name)
          current-idx   (p/find-index phase-routes (partial = current-route))
          next-idx      (p/find-index phase-routes (partial = next-route))
          ani-dir       (if (< next-idx current-idx)
                          :previous
                          :next)]
      {:db            (assoc db ::phase-state {:animation-direction ani-dir})
       :dispatch      [::navigation/scroll-to-top]
       :route/replace (p/assoc-if {:route  next-route
                                   :params {:workspace-id (-> match
                                                              :parameters
                                                              :path
                                                              :workspace-id)}}
                                  :query (ws-util/search-origin-query-params match))})))

(rf/reg-sub ::phase-state
  :-> ::phase-state)

(rf/reg-sub ::current-phase-index
  :<- [:route/match]
  :<- [::phases]
  (fn [[match phases] _]
    (or (phase->index (->> phases
                           (mapv (fn [{:keys [enabled? name]}]
                                   (when enabled? name))))
                      (-> match :data :phase))
        0)))

(rf/reg-sub ::animation-direction
  :<- [::phase-state]
  (fn [state _]
    (:animation-direction state :none)))

;; The phase-entity (app, exec etc) that is currently selected as visible in the
;; UI - i.e. not necessarily the one that the process is on.
(rf/reg-sub ::current-child
  :<- [::phases]
  :<- [::current-phase-index]
  :<- [::ws-subs/pre-consultation]
  :<- [::ws-subs/application]
  :<- [::ws-subs/execution]
  :<- [::ws-subs/periodic-parent]
  :<- [::ws-subs/archiving]
  (fn [[phases current-phase-index pre-consultation application execution periodic-parent archiving] _]
    (case (and (> (count phases) current-phase-index)
               (-> phases (get current-phase-index) :name))
      :workspace.state/pre-consultation pre-consultation
      :workspace.state/application application
      :workspace.state/execution execution
      :workspace.state/periodic-parent periodic-parent
      :workspace.state/archiving archiving
      nil)))
